import { useEffect, useRef, useState, type ChangeEvent, type CSSProperties, type ReactNode } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Emoji from '@tiptap/extension-emoji';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import Image from '@tiptap/extension-image';
import {
  RiBold,
  RiItalic,
  RiStrikethrough,
  RiCodeSSlashLine,
  RiListUnordered,
  RiListOrdered,
  RiDoubleQuotesL,
  RiCodeBoxLine,
  RiSeparator,
  RiFormatClear,
  RiLinkM,
  RiLinkUnlinkM,
  RiArrowGoBackLine,
  RiArrowGoForwardLine,
} from 'react-icons/ri';
import { MdKeyboardReturn, MdFormatColorText, MdFormatColorFill, MdAddPhotoAlternate } from 'react-icons/md';
import './style.css';
import { EmojiPickerButton } from '../EmojiPickerButton';

const ResizableImage = (Image as any).extend({
  addAttributes() {
    return {
      ...(this.parent ? this.parent() : {}),
      width: {
        default: '100%',
        parseHTML: (element: HTMLElement) => element.getAttribute('data-width') || '100%',
        renderHTML: (attributes: { width?: string }) => ({
          'data-width': attributes.width || '100%',
          style: `width: ${attributes.width || '100%'}; height: auto;`,
        }),
      },
    };
  },
  addNodeView() {
    return ({ node, editor, getPos }: any) => {
      let currentNode = node;
      const wrapper = document.createElement('span');
      wrapper.className = 'text-editor-image-node';
      wrapper.style.width = currentNode.attrs.width || '100%';
      wrapper.setAttribute('contenteditable', 'false');

      const image = document.createElement('img');
      image.src = currentNode.attrs.src;
      image.alt = currentNode.attrs.alt || '';
      image.title = currentNode.attrs.title || '';
      image.draggable = false;

      const createHandle = (direction: 'nw' | 'ne' | 'sw' | 'se') => {
        const handle = document.createElement('span');
        handle.className = `text-editor-image-handle text-editor-image-handle--${direction}`;

        const onMouseDown = (event: MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();

          const wrapperRect = wrapper.getBoundingClientRect();
          const parentRect = wrapper.parentElement?.getBoundingClientRect();
          const parentWidth = parentRect?.width || wrapperRect.width;
          const startX = event.clientX;
          const startWidth = wrapperRect.width;

          const onMouseMove = (moveEvent: MouseEvent) => {
            const delta = moveEvent.clientX - startX;
            const isLeftHandle = direction === 'nw' || direction === 'sw';
            const nextWidth = startWidth + (isLeftHandle ? -delta : delta);
            const clampedWidth = Math.max(80, Math.min(parentWidth, nextWidth));
            const widthPercent = Math.round((clampedWidth / parentWidth) * 100);

            wrapper.style.width = `${widthPercent}%`;
          };

          const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);

            const pos = typeof getPos === 'function' ? getPos() : null;

            if (typeof pos === 'number') {
              editor
                .chain()
                .focus()
                .command(({ tr }: any) => {
                  tr.setNodeMarkup(pos, undefined, {
                    ...currentNode.attrs,
                    width: wrapper.style.width || '100%',
                  });
                  return true;
                })
                .run();
            }
          };

          document.addEventListener('mousemove', onMouseMove);
          document.addEventListener('mouseup', onMouseUp);
        };

        handle.addEventListener('mousedown', onMouseDown);
        return handle;
      };

      wrapper.appendChild(image);
      wrapper.appendChild(createHandle('nw'));
      wrapper.appendChild(createHandle('ne'));
      wrapper.appendChild(createHandle('sw'));
      wrapper.appendChild(createHandle('se'));

      return {
        dom: wrapper,
        update: (updatedNode: any) => {
          if (updatedNode.type.name !== currentNode.type.name) {
            return false;
          }

          currentNode = updatedNode;
          wrapper.style.width = currentNode.attrs.width || '100%';
          image.src = currentNode.attrs.src;
          image.alt = currentNode.attrs.alt || '';
          image.title = currentNode.attrs.title || '';
          return true;
        },
        selectNode: () => {
          wrapper.classList.add('is-selected');
        },
        deselectNode: () => {
          wrapper.classList.remove('is-selected');
        },
      };
    };
  },
});

const FontSize = (Extension as any).create({
  name: 'fontSize',
  addOptions() {
    return {
      types: ['textStyle'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element: HTMLElement) => element.style.fontSize || null,
            renderHTML: (attributes: { fontSize?: string | null }) => {
              if (!attributes.fontSize) {
                return {};
              }

              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }: any) =>
          chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }: any) =>
          chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

const FONT_SIZE_OPTIONS = [
  '12px',
  '14px',
  '16px',
  '18px',
  '20px',
  '24px',
  '28px',
  '32px',
  '36px',
  '48px',
  '64px',
  '72px',
];
const HEADING_OPTIONS = [
  { label: 'Paragraph', value: 'paragraph' },
  { label: 'Heading 1', value: 'h1' },
  { label: 'Heading 2', value: 'h2' },
  { label: 'Heading 3', value: 'h3' },
  { label: 'Heading 4', value: 'h4' },
  { label: 'Heading 5', value: 'h5' },
];
const FONT_FAMILY_OPTIONS = [
  { label: 'Default', value: '' },
  { label: 'System UI', value: 'system-ui, -apple-system, "Segoe UI", sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Segoe UI', value: '"Segoe UI", Tahoma, sans-serif' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", serif' },
  { label: 'Palatino', value: '"Palatino Linotype", "Book Antiqua", Palatino, serif' },
  { label: 'Garamond', value: 'Garamond, serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Trebuchet MS', value: '"Trebuchet MS", sans-serif' },
  { label: 'Gill Sans', value: '"Gill Sans", "Gill Sans MT", Calibri, sans-serif' },
  { label: 'Lucida Sans', value: '"Lucida Sans Unicode", "Lucida Grande", sans-serif' },
  { label: 'Courier', value: '"Courier New", monospace' },
  { label: 'Consolas', value: 'Consolas, "Lucida Console", Monaco, monospace' },
  { label: 'Monaco', value: 'Monaco, monospace' },
  { label: 'Tahoma', value: 'Tahoma, sans-serif' },
  { label: 'Impact', value: 'Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif' },
  { label: 'Comic Sans MS', value: '"Comic Sans MS", "Comic Sans", cursive' },
];

type Props = {
  value: string;
  onChange: (html: string, text: string) => void;
  error?: boolean;
};

type IconButtonProps = {
  title: string;
  isActive?: boolean;
  disabled?: boolean;
  onClick: () => void;
  icon: ReactNode;
};

type DropdownOption = {
  label: string;
  value: string;
  style?: CSSProperties;
};

type ToolbarDropdownProps = {
  title: string;
  valueLabel: string;
  options: DropdownOption[];
  value: string;
  onSelect: (value: string) => void;
  menuClassName?: string;
};

const IconButton = ({ title, isActive, disabled, onClick, icon }: IconButtonProps) => (
  <button
    type="button"
    className={`text-editor__btn ${isActive ? 'is-active' : ''}`}
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
  >
    {icon}
  </button>
);

const ToolbarDropdown = ({ title, valueLabel, options, value, onSelect, menuClassName }: ToolbarDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, []);

  return (
    <div ref={dropdownRef} className="text-editor__dropdown">
      <button
        type="button"
        className="text-editor__dropdown-trigger"
        title={title}
        aria-label={title}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="text-editor__dropdown-label">{valueLabel}</span>
        <span className="text-editor__dropdown-caret">▼</span>
      </button>

      {isOpen ? (
        <div className={`text-editor__dropdown-menu ${menuClassName || ''}`}>
          {options.map((option) => (
            <button
              key={`${title}-${option.label}`}
              type="button"
              className={`text-editor__dropdown-item ${value === option.value ? 'is-active' : ''}`}
              style={option.style}
              onClick={() => {
                onSelect(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const normalizeColorValue = (value?: string, fallback = '#19334d') => {
  if (!value) {
    return fallback;
  }

  if (value.startsWith('#')) {
    if (value.length === 4) {
      return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
    }

    return value;
  }

  const rgbMatch = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);

  if (!rgbMatch) {
    return fallback;
  }

  const [red, green, blue] = rgbMatch.slice(1, 4).map((channel) => Number(channel));

  return `#${[red, green, blue].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
};

const normalizeAndValidateUrl = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return '';
  }

  const normalizedValue = /^(https?:|mailto:|tel:)/i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`;

  try {
    const parsedUrl = new URL(normalizedValue);
    const allowedProtocols = ['http:', 'https:', 'mailto:', 'tel:'];

    if (!allowedProtocols.includes(parsedUrl.protocol)) {
      return '';
    }

    return normalizedValue;
  } catch {
    return '';
  }
};

const TextEditor = ({ value, onChange, error }: Props) => {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const editor = useEditor({
    extensions: [
      TextStyle as any,
      FontSize as any,
      FontFamily.configure({ types: ['textStyle'] }) as any,
      Color.configure({ types: ['textStyle'] }) as any,
      Highlight.configure({ multicolor: true }) as any,
      Link.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
      }) as any,
      Emoji.configure({ enableEmoticons: true }) as any,
      ResizableImage as any,
      StarterKit as any,
    ],
    content: value || '<p></p>',
    onUpdate: (params: any) => {
      const html = params.editor.getHTML();
      const text = params.editor.getText({ blockSeparator: '\n' });
      onChange(html, text);
    },
  } as any) as any;

  useEffect(() => {
    if (!editor) {
      return;
    }

    const currentHtml = editor.getHTML();
    const nextValue = value || '<p></p>';

    if (currentHtml !== nextValue) {
      editor.commands.setContent(nextValue, false);
    }
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  const currentColor = normalizeColorValue(editor.getAttributes('textStyle').color);
  const currentBackgroundColor = normalizeColorValue(editor.getAttributes('highlight').color, '#fff59d');
  const currentFontSize = editor.getAttributes('textStyle').fontSize || '16px';
  const currentFontFamily = editor.getAttributes('textStyle').fontFamily || '';
  const currentHeadingValue = editor.isActive('heading', { level: 1 })
    ? 'h1'
    : editor.isActive('heading', { level: 2 })
      ? 'h2'
      : editor.isActive('heading', { level: 3 })
        ? 'h3'
        : 'paragraph';
  const currentHeadingLabel =
    HEADING_OPTIONS.find((option) => option.value === currentHeadingValue)?.label || 'Paragraph';
  const currentFamilyOption = FONT_FAMILY_OPTIONS.find((option) => option.value === currentFontFamily);
  const currentFamilyLabel = currentFamilyOption?.label || 'Default';
  const isLinkActive = editor.isActive('link');

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href || '';
    const nextUrl = window.prompt('Enter URL', previousUrl);

    if (nextUrl === null) {
      return;
    }

    const trimmedUrl = nextUrl.trim();

    if (!trimmedUrl) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    const normalizedUrl = normalizeAndValidateUrl(trimmedUrl);

    if (!normalizedUrl) {
      window.alert('Please enter a valid URL (http, https, mailto, or tel).');
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({
        href: normalizedUrl,
        target: '_blank',
        rel: 'noopener noreferrer nofollow',
      })
      .run();
  };

  const toggleLink = () => {
    if (isLinkActive) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    setLink();
  };

  const insertImage = () => {
    imageInputRef.current?.click();
  };

  const onImageSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file || !file.type.startsWith('image/')) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const imageDataUrl = typeof reader.result === 'string' ? reader.result : '';

      if (!imageDataUrl) {
        return;
      }

      editor.chain().focus().setImage({ src: imageDataUrl }).run();
    };

    reader.readAsDataURL(file);
    event.target.value = '';
  };

  return (
    <div className={`text-editor ${error ? 'text-editor--error' : ''}`}>
      <div className="text-editor__toolbar">
        <div className="text-editor__toolbar-group">
          <IconButton
            title="Undo"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().chain().focus().undo().run()}
            icon={<RiArrowGoBackLine />}
          />
          <IconButton
            title="Redo"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().chain().focus().redo().run()}
            icon={<RiArrowGoForwardLine />}
          />
        </div>

        <div className="text-editor__toolbar-group">
          <ToolbarDropdown
            title="Heading style"
            value={currentHeadingValue}
            valueLabel={currentHeadingLabel}
            options={HEADING_OPTIONS}
            onSelect={(headingValue) => {
              if (headingValue === 'paragraph') {
                editor.chain().focus().setParagraph().run();
                return;
              }

              const level = Number(headingValue.replace('h', ''));

              editor.chain().focus().setHeading({ level }).run();
            }}
            menuClassName="text-editor__dropdown-menu--heading"
          />
          <ToolbarDropdown
            title="Text size"
            value={currentFontSize}
            valueLabel={currentFontSize}
            options={FONT_SIZE_OPTIONS.map((size) => ({ label: size, value: size }))}
            onSelect={(size) => editor.chain().focus().setFontSize(size).run()}
            menuClassName="text-editor__dropdown-menu--size"
          />
          <ToolbarDropdown
            title="Text family"
            value={currentFontFamily}
            valueLabel={currentFamilyLabel}
            options={FONT_FAMILY_OPTIONS.map((family) => ({
              label: family.label,
              value: family.value,
              style: family.value ? { fontFamily: family.value } : undefined,
            }))}
            onSelect={(nextValue) => {
              if (!nextValue) {
                editor.chain().focus().unsetFontFamily().run();
                return;
              }

              editor.chain().focus().setFontFamily(nextValue).run();
            }}
            menuClassName="text-editor__dropdown-menu--family"
          />
          <label
            className="text-editor__color-control"
            title="Text color"
            aria-label="Text color"
            style={{ '--editor-current-color': currentColor } as CSSProperties}
          >
            <span className="text-editor__color-icon">
              <MdFormatColorText />
            </span>
            <input
              type="color"
              className="text-editor__color-input"
              value={currentColor}
              onChange={(event) => editor.chain().focus().setColor(event.target.value).run()}
            />
          </label>
          <label
            className="text-editor__color-control"
            title="Text background color"
            aria-label="Text background color"
            style={{ '--editor-current-color': currentBackgroundColor } as CSSProperties}
          >
            <span className="text-editor__color-icon">
              <MdFormatColorFill />
            </span>
            <input
              type="color"
              className="text-editor__color-input"
              value={currentBackgroundColor}
              onChange={(event) => editor.chain().focus().setHighlight({ color: event.target.value }).run()}
            />
          </label>
          <IconButton
            title="Clear formatting"
            onClick={() =>
              editor.chain().focus().clearNodes().unsetAllMarks().unsetColor().unsetHighlight().unsetFontSize().run()
            }
            icon={<RiFormatClear />}
          />
        </div>

        <div className="text-editor__toolbar-group">
          <IconButton
            title="Bold"
            isActive={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
            icon={<RiBold />}
          />
          <IconButton
            title="Italic"
            isActive={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            icon={<RiItalic />}
          />
          <IconButton
            title="Strikethrough"
            isActive={editor.isActive('strike')}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            icon={<RiStrikethrough />}
          />
          <IconButton
            title="Inline code"
            isActive={editor.isActive('code')}
            onClick={() => editor.chain().focus().toggleCode().run()}
            icon={<RiCodeSSlashLine />}
          />
          <IconButton
            title={isLinkActive ? 'Remove link' : 'Link'}
            isActive={isLinkActive}
            onClick={toggleLink}
            icon={isLinkActive ? <RiLinkUnlinkM /> : <RiLinkM />}
          />
        </div>

        <div className="text-editor__toolbar-group">
          <IconButton
            title="Bullet list"
            isActive={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            icon={<RiListUnordered />}
          />
          <IconButton
            title="Ordered list"
            isActive={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            icon={<RiListOrdered />}
          />
          <IconButton
            title="Blockquote"
            isActive={editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            icon={<RiDoubleQuotesL />}
          />
          <IconButton
            title="Code block"
            isActive={editor.isActive('codeBlock')}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            icon={<RiCodeBoxLine />}
          />
          <IconButton
            title="Horizontal rule"
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            icon={<RiSeparator />}
          />
          <IconButton
            title="Line break"
            onClick={() => editor.chain().focus().setHardBreak().run()}
            icon={<MdKeyboardReturn />}
          />
        </div>

        <div className="text-editor__toolbar-group">
          <EmojiPickerButton
            onSelect={(emoji) => {
              editor.chain().focus().insertContent(emoji).run();
            }}
          />
          <IconButton title="Upload image" onClick={insertImage} icon={<MdAddPhotoAlternate />} />
        </div>
      </div>

      <div className={`text-editor__content`}>
        <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onImageSelect} />
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default TextEditor;
